import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

type TextLinkVariant = 'inline' | 'quiet';

interface TextLinkBaseProps {
  variant?: TextLinkVariant;
  className?: string;
  children: ReactNode;
}

interface InternalTextLinkProps extends TextLinkBaseProps {
  /** Internal route — renders a react-router `Link`. */
  to: string;
  href?: never;
}

interface ExternalTextLinkProps extends TextLinkBaseProps {
  to?: never;
  /** External URL — renders an `<a>`. */
  href: string;
}

export type TextLinkProps = InternalTextLinkProps | ExternalTextLinkProps;

const VARIANT_CLASSES: Record<TextLinkVariant, string> = {
  // In-copy link: ink, underlined, the underline warms to accent on hover.
  inline:
    'font-medium text-ink underline decoration-border decoration-2 underline-offset-4 transition-colors duration-150 hover:decoration-accent',
  // Meta links, breadcrumbs, "N replies": quiet secondary, underlines on hover.
  quiet: 'text-ink-secondary transition-colors duration-150 hover:text-ink hover:underline underline-offset-4',
};

/**
 * The one in-copy / affordance link (ui-ux.md §2) — no screen should reach for
 * amber (`text-accent-ink`) as a link colour. Internal (`to`) renders a
 * react-router `Link`; external (`href`) renders an `<a>`.
 */
export function TextLink({
  to,
  href,
  variant = 'inline',
  className,
  children,
}: TextLinkProps) {
  const classes = cn(VARIANT_CLASSES[variant], className);
  if (to) {
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={classes}>
      {children}
    </a>
  );
}
